/* ==========================================================================
   KOPSTORM SEARCH (14 Aug 2026)
   Client side product search: full index via the Section Rendering API,
   typo tolerant, synonym aware and model aware matching, prices rendered
   server side so results always match the session market and currency.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('kop-search')) return;

  var INDEX_SECTION = 'kop-search-data';
  var CACHE_TTL = 30 * 60 * 1000; /* 30 minutes */
  var CACHE_VERSION = 2; /* bumped 18 Aug 2026: index items gained the pd field */
  var RECENT_KEY = 'kop_recent_searches';
  var RECENT_MAX = 6;

  /* Built in synonym groups. Every member expands to the whole group.
     Cross language on purpose: matching runs against product data, which is
     mostly Dutch/English, while shoppers type in any storefront language. */
  /* Terms that express "show me what is discounted". Used both as a synonym group
     and, when saleFirst is on, to lift products that actually carry a discount. */
  var SALE_TERMS = ['sale', 'outlet', 'korting', 'uitverkoop', 'aanbieding', 'sonderangebot', 'soldes', 'discount'];

  var SYNONYMS = [
    ['hoesje', 'hoesjes', 'hoes', 'case', 'cases', 'cover', 'covers', 'hulle', 'huelle', 'etui', 'coque'],
    ['koord', 'koordje', 'koorden', 'cord', 'cords', 'strap', 'straps', 'kette', 'chaine', 'ketting', 'chain'],
    ['telefoonkoord', 'telefoonkoordje', 'crossbody', 'necklace'],
    ['laptophoes', 'laptophoezen', 'sleeve', 'laptoptas', 'laptophulle'],
    ['portemonnee', 'wallet', 'pasjeshouder', 'cardholder', 'kaarthouder', 'geldborse'],
    ['tas', 'tassen', 'bag', 'bags', 'tasche', 'sac', 'etuitje'],
    ['oplader', 'charger', 'kabel', 'cable', 'ladegerat', 'chargeur'],
    ['telefoon', 'phone', 'smartphone', 'handy', 'gsm'],
    ['samsung', 'galaxy'],
    SALE_TERMS,
    ['goud', 'gold', 'goldy', 'dore'],
    ['zilver', 'silver', 'argent', 'silber'],
    ['zwart', 'black', 'noir', 'schwarz'],
    ['wit', 'white', 'blanc', 'weiss'],
    ['roze', 'pink', 'rose', 'rosa'],
    ['groen', 'green', 'grun', 'vert'],
    ['blauw', 'blue', 'blau', 'bleu'],
    ['rood', 'red', 'rot', 'rouge'],
    ['bruin', 'brown', 'braun', 'marron'],
    ['paars', 'purple', 'lila', 'violet'],
    ['geel', 'yellow', 'gelb', 'jaune'],
    ['oranje', 'orange'],
    ['grijs', 'grey', 'gray', 'grau', 'gris']
  ];

  /* ----------------------------------------------------------------------
     Text helpers
     ---------------------------------------------------------------------- */

  function normalize(str) {
    return String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Damerau-Levenshtein with early exit above the cap. */
  function editDistance(a, b, cap) {
    var la = a.length;
    var lb = b.length;
    if (Math.abs(la - lb) > cap) return cap + 1;
    var prevPrev = [];
    var prev = [];
    var cur = [];
    var i;
    var j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      var rowMin = cur[0];
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var val = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          val = Math.min(val, prevPrev[j - 2] + 1);
        }
        cur[j] = val;
        if (val < rowMin) rowMin = val;
      }
      if (rowMin > cap) return cap + 1;
      prevPrev = prev.slice();
      prev = cur.slice();
    }
    return prev[lb];
  }

  function fuzzyCap(len) {
    if (len >= 8) return 2;
    if (len >= 4) return 1;
    return 0;
  }

  /* ----------------------------------------------------------------------
     Synonym expansion
     ---------------------------------------------------------------------- */

  function buildSynonymMap(extra) {
    var groups = SYNONYMS.slice();
    if (extra && typeof extra === 'string') {
      extra.split(/\r?\n/).forEach(function (line) {
        var members = line.split(',').map(function (m) { return normalize(m); }).filter(Boolean);
        if (members.length > 1) groups.push(members);
      });
    }
    var map = {};
    groups.forEach(function (group) {
      var normGroup = group.map(function (m) { return normalize(m); }).filter(Boolean);
      normGroup.forEach(function (member) {
        if (!map[member]) map[member] = [];
        normGroup.forEach(function (other) {
          if (other !== member && map[member].indexOf(other) === -1) map[member].push(other);
        });
      });
    });
    return map;
  }

  /* ----------------------------------------------------------------------
     Matching and scoring
     ---------------------------------------------------------------------- */

  function prepareItem(item) {
    item._title = normalize(item.t);
    item._titleWords = item._title ? item._title.split(' ') : [];
    item._type = normalize(item.ty);
    item._typeWords = item._type ? item._type.split(' ') : [];
    item._opts = normalize(item.ov);
    item._optWords = item._opts ? item._opts.split(' ') : [];
    item._tags = normalize(item.tg);
    item._tagWords = item._tags ? item._tags.split(' ') : [];
    return item;
  }

  function bestWordScore(token, words, prefixScore, fuzzyScore) {
    var best = 0;
    var cap = fuzzyCap(token.length);
    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      if (word === token) {
        best = Math.max(best, prefixScore + 2);
      } else if (word.indexOf(token) === 0) {
        best = Math.max(best, prefixScore);
      } else if (cap > 0 && best < fuzzyScore) {
        var against = word.length > token.length + cap ? word.slice(0, token.length + cap) : word;
        if (editDistance(token, against, cap) <= cap) best = Math.max(best, fuzzyScore);
      }
      if (best >= prefixScore + 2) break;
    }
    return best;
  }

  /* Recency bonus (18 Aug 2026). Newer products should surface above older ones
     of comparable relevance, so a published product gets a small bounded bonus
     that can never out rank an exact title match on an extra token. */
  var RECENCY_STEPS = {
    off: [],
    light: [[90, 2], [365, 1]],
    strong: [[90, 5], [365, 3], [730, 1]]
  };

  function recencyBonus(item, steps, nowSeconds) {
    if (!steps || !steps.length || !item.pd) return 0;
    var ageDays = (nowSeconds - item.pd) / 86400;
    for (var i = 0; i < steps.length; i++) {
      if (ageDays <= steps[i][0]) return steps[i][1];
    }
    return 0;
  }

  function scoreItem(item, tokens, synonymMap) {
    var total = 0;
    for (var i = 0; i < tokens.length; i++) {
      var alts = [tokens[i]].concat(synonymMap[tokens[i]] || []);
      var best = 0;
      for (var a = 0; a < alts.length && best < 12; a++) {
        var token = alts[a];
        var hit = bestWordScore(token, item._titleWords, 10, 6);
        if (hit < 12) hit = Math.max(hit, bestWordScore(token, item._typeWords, 7, 4));
        if (hit < 12) hit = Math.max(hit, bestWordScore(token, item._optWords, 6, 3));
        if (hit < 12) hit = Math.max(hit, bestWordScore(token, item._tagWords, 4, 2));
        if (a > 0 && hit > 0) hit = Math.max(1, hit - 2); /* synonyms rank below the literal term */
        best = Math.max(best, hit);
      }
      if (best === 0) return 0; /* every token must match somewhere */
      total += best;
    }
    if (item._title.indexOf(tokens.join(' ')) === 0) total += 8;
    if (item.av) total += 2;
    return total;
  }

  /* ----------------------------------------------------------------------
     Custom element
     ---------------------------------------------------------------------- */

  class KopSearchElement extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      this.config = JSON.parse(this.querySelector('[data-kop-config]').textContent);
      this.input = this.querySelector('.kop-search__input');
      this.body = this.querySelector('.kop-search__body');
      this.states = {};
      var self = this;
      this.querySelectorAll('[data-kop-state]').forEach(function (el) {
        self.states[el.getAttribute('data-kop-state')] = el;
      });
      this.resultsEl = this.querySelector('[data-kop-results]');
      this.collectionsEls = this.querySelectorAll('[data-kop-collections], [data-kop-collections-empty]');
      this.countEl = this.querySelector('[data-kop-count]');
      this.allLink = this.querySelector('[data-kop-all]');
      this.allLinkEmpty = this.querySelector('[data-kop-all-empty]');
      this.noneEl = this.querySelector('[data-kop-none]');
      this.recentWrap = this.querySelector('[data-kop-recent-wrap]');
      this.recentList = this.querySelector('[data-kop-recent]');
      this.clearBtn = this.querySelector('[data-kop-clear]');

      this.synonymMap = buildSynonymMap(this.config.extraSynonyms);
      /* 11 Sep 2026: besides the chip title, each chip is also matched on its parent
         menu titles (c) and on the handle in its URL, so "samsung s25" finds the
         "S25 Cases" link that sits under "Samsung Cases". */
      this.collections = (this.config.collections || []).map(function (c) {
        var handle = String(c.u || '').split(/[?#]/)[0].split('/').filter(Boolean).pop() || '';
        var words = normalize(c.t);
        return {
          t: c.t,
          u: c.u,
          _n: words,
          _w: words ? words.split(' ') : [],
          _cw: normalize((c.c || '') + ' ' + handle.replace(/-/g, ' ')).split(' ').filter(Boolean)
        };
      });

      this.index = null;
      this.indexPromise = null;
      this.navIndex = -1;
      this.escapeArmed = false;
      this._debounce = null;

      this.bindEvents();
      this.renderRecent();
    }

    /* ------------------------------------------------------------------ */

    bindEvents() {
      var self = this;

      this.addEventListener('click', function (event) {
        var closeEl = event.target.closest('[data-kop-close]');
        if (closeEl) { self.close(); return; }

        var chip = event.target.closest('[data-kop-term]');
        if (chip) { self.setQuery(chip.getAttribute('data-kop-term')); return; }

        var removeBtn = event.target.closest('[data-kop-remove]');
        if (removeBtn) {
          event.preventDefault();
          event.stopPropagation();
          self.removeRecent(removeBtn.getAttribute('data-kop-remove'));
          return;
        }

        var link = event.target.closest('a[href]');
        if (link && self.input.value.trim()) self.saveRecent(self.input.value.trim());
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () { self.setQuery(''); self.input.focus(); });
      }

      this.input.addEventListener('input', function () { self.onQueryChanged(); });

      this.input.form.addEventListener('submit', function (event) {
        var query = self.input.value.trim();
        if (!query) { event.preventDefault(); return; }
        /* Enter on a highlighted row opens that row instead of submitting. */
        var active = self.activeNavItem();
        if (active) {
          event.preventDefault();
          self.saveRecent(query);
          active.click();
          return;
        }
        self.saveRecent(query);
        /* let the native submit reach the full search page */
      });

      this.input.addEventListener('keydown', function (event) { self.onKeydown(event); });

      /* Take over the theme's header search triggers. The theme binds its own
         drawer handler on the element itself, so document capture wins. */
      document.addEventListener('click', function (event) {
        var trigger = event.target.closest('a[aria-controls^="header-search-"], .header__search-link a');
        if (!trigger) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        self.open();
      }, true);

      /* Warm the index as soon as a search trigger is approached. */
      document.addEventListener('pointerenter', function (event) {
        if (event.target.nodeType !== 1) return;
        if (event.target.closest && event.target.closest('.header__search-link')) self.ensureIndex();
      }, true);
    }

    /* ------------------------------------------------------------------
       Open / close
       ------------------------------------------------------------------ */

    open() {
      if (!this.hidden) return;
      this.hidden = false;
      document.documentElement.classList.add('kop-search-open');
      this.lockScroll();
      this.renderRecent();
      this.ensureIndex();
      var self = this;
      requestAnimationFrame(function () { self.input.focus(); });
      this.onQueryChanged(true);
    }

    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.documentElement.classList.remove('kop-search-open');
      this.unlockScroll();
      this.escapeArmed = false;
      this.setNav(-1);
    }

    lockScroll() {
      this._scrollY = window.scrollY;
      var gutter = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.position = 'fixed';
      document.body.style.top = -this._scrollY + 'px';
      document.body.style.width = '100%';
      if (gutter > 0) document.body.style.paddingRight = gutter + 'px';
    }

    unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      window.scrollTo({ top: this._scrollY || 0, behavior: 'instant' });
    }

    /* ------------------------------------------------------------------
       Index loading
       ------------------------------------------------------------------ */

    cacheKey() {
      return 'kop_search_idx:' + [this.config.locale, this.config.country, this.config.currency].join(':');
    }

    ensureIndex() {
      if (this.index) return Promise.resolve(this.index);
      if (this.indexPromise) return this.indexPromise;

      var self = this;
      var cached = null;
      try {
        cached = JSON.parse(localStorage.getItem(this.cacheKey()));
      } catch (error) { cached = null; }
      if (cached && cached.v === CACHE_VERSION && Date.now() - cached.ts < CACHE_TTL) {
        this.index = cached.items.map(prepareItem);
        return Promise.resolve(this.index);
      }

      this.indexPromise = this.fetchIndexPage(1).then(function (first) {
        var pagePromises = [];
        for (var page = 2; page <= first.pages; page++) pagePromises.push(self.fetchIndexPage(page));
        return Promise.all(pagePromises).then(function (rest) {
          var items = first.items;
          rest.forEach(function (chunk) { items = items.concat(chunk.items); });
          try {
            localStorage.setItem(self.cacheKey(), JSON.stringify({ v: CACHE_VERSION, ts: Date.now(), items: items }));
          } catch (error) { /* storage full: run without cache */ }
          self.index = items.map(prepareItem);
          self.indexPromise = null;
          return self.index;
        });
      }).catch(function (error) {
        self.indexPromise = null;
        throw error;
      });
      return this.indexPromise;
    }

    fetchIndexPage(page) {
      var url = this.config.indexUrl + '?section_id=' + INDEX_SECTION + '&page=' + page;
      return fetch(url).then(function (response) { return response.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var script = doc.querySelector('[data-kop-search-index]');
        if (!script) throw new Error('kop-search: index section missing');
        return JSON.parse(script.textContent);
      });
    }

    /* ------------------------------------------------------------------
       Query handling and rendering
       ------------------------------------------------------------------ */

    setQuery(value) {
      /* Programmatic value writes fire no input event, so every code path
         that fills the field must run through here. */
      this.input.value = value;
      this.onQueryChanged(true);
      this.input.focus();
    }

    onQueryChanged(immediate) {
      var self = this;
      if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
      window.clearTimeout(this._debounce);
      if (immediate) { this.applyQuery(); return; }
      this._debounce = window.setTimeout(function () { self.applyQuery(); }, 120);
    }

    applyQuery() {
      var query = normalize(this.input.value);
      this.setNav(-1);
      this.escapeArmed = false;

      if (query.length < 2) { this.showState('browse'); return; }

      if (!this.index) {
        this.showState('loading');
        var self = this;
        this.ensureIndex().then(function () {
          if (!self.hidden) self.applyQuery();
        }).catch(function () {
          /* index unreachable: hand over to the native search page */
          if (self.input.value.trim()) self.input.form.submit();
        });
        return;
      }

      var tokens = query.split(' ');
      var matches = [];
      var steps = RECENCY_STEPS[this.config.recency] || RECENCY_STEPS.light;
      var nowSeconds = Date.now() / 1000;
      var saleIntent = this.config.saleFirst !== false && tokens.some(function (token) {
        return SALE_TERMS.indexOf(token) !== -1;
      });
      for (var i = 0; i < this.index.length; i++) {
        var item = this.index[i];
        var score = scoreItem(item, tokens, this.synonymMap);
        if (score <= 0) continue;
        score += recencyBonus(item, steps, nowSeconds);
        /* On a sale style query, products that really are discounted come first. */
        if (saleIntent && item.sale) score += 20;
        matches.push({ item: item, score: score });
      }
      matches.sort(function (a, b) {
        if (b.score !== a.score) return b.score - a.score;
        if (a.item.av !== b.item.av) return a.item.av ? -1 : 1;
        if ((b.item.pd || 0) !== (a.item.pd || 0)) return (b.item.pd || 0) - (a.item.pd || 0);
        return a.item._title.localeCompare(b.item._title);
      });

      if (matches.length === 0) { this.renderEmpty(); return; }
      this.renderResults(matches, tokens);
    }

    showState(name) {
      var states = this.states;
      Object.keys(states).forEach(function (key) { states[key].hidden = key !== name; });
      this.body.scrollTop = 0;
    }

    renderResults(matches, tokens) {
      var self = this;
      var limit = this.config.maxResults || 12;
      var shown = matches.slice(0, limit);

      this.resultsEl.textContent = '';
      shown.forEach(function (match, position) {
        self.resultsEl.appendChild(self.buildCard(match.item, tokens, position));
      });

      this.countEl.textContent = String(matches.length);

      var searchHref = this.config.searchUrl + '?q=' + encodeURIComponent(this.input.value.trim());
      this.allLink.href = searchHref;
      this.allLink.textContent = this.config.copy.all.replace('[count]', String(matches.length));

      this.renderCollectionChips(tokens);

      this.showState('results');
    }

    /* Matching collections as chips, rendered into both the results and the empty
       state: a query like "sale" or "outlet" can have no product hits while the
       landing page the shopper is after does exist. Chip order follows the config,
       which starts with the merchant curated menus. */
    renderCollectionChips(tokens) {
      var self = this;
      var chipMax = this.config.chipMax || 6;
      tokens = (tokens || []).filter(Boolean);
      if (!tokens.length) tokens = [''];

      function tokenHits(token, words) {
        var alts = [token].concat(self.synonymMap[token] || []);
        return alts.some(function (alt) {
          return words.some(function (word) { return word.indexOf(alt) === 0; });
        });
      }

      /* Every token must match the chip title or its context (parent menu titles and
         URL handle), and at least one token must match the title itself so a broad
         word such as "samsung" does not list every sub link of the Samsung menu.
         Chips matched fully on their own title keep priority, config order after that. */
      var full = [];
      var partial = [];
      for (var i = 0; i < this.collections.length; i++) {
        var col = this.collections[i];
        var inTitle = 0;
        var ok = tokens.every(function (token) {
          if (tokenHits(token, col._w)) { inTitle++; return true; }
          return tokenHits(token, col._cw);
        });
        if (!ok || inTitle === 0) continue;
        if (inTitle === tokens.length) full.push(col); else partial.push(col);
      }
      var built = full.concat(partial).slice(0, chipMax);
      this.collectionsEls.forEach(function (wrap) {
        wrap.textContent = '';
        built.forEach(function (col) {
          var chip = document.createElement('a');
          chip.className = 'kop-search__chip kop-search__chip--collection';
          chip.href = col.u;
          chip.textContent = col.t;
          chip.setAttribute('data-kop-nav', '');
          wrap.appendChild(chip);
        });
        wrap.hidden = built.length === 0;
        /* 19 Aug 2026: in the results state the chips live inside a titled section
           ("Collections" above the product grid); hide and show that section as one. */
        var outer = wrap.closest('[data-kop-collections-wrap]');
        if (outer) outer.hidden = built.length === 0;
      });
    }

    renderEmpty() {
      this.renderCollectionChips(normalize(this.input.value).split(' '));
      this.noneEl.textContent = this.config.copy.none.replace('[q]', this.input.value.trim());
      var searchHref = this.config.searchUrl + '?q=' + encodeURIComponent(this.input.value.trim());
      this.allLinkEmpty.href = searchHref;
      this.allLinkEmpty.textContent = this.config.copy.all.replace('[count]', '').replace('  ', ' ');
      this.showState('empty');
    }

    buildCard(item, tokens, position) {
      var card = document.createElement('a');
      card.className = 'kop-search__card';
      card.href = item.u;
      card.setAttribute('data-kop-nav', '');
      card.id = 'kop-search-option-' + position;
      card.setAttribute('role', 'option');

      var thumb = document.createElement('span');
      thumb.className = 'kop-search__thumb';
      if (item.img) {
        var img = document.createElement('img');
        img.src = item.img;
        img.alt = item.t;
        img.loading = 'lazy';
        img.width = 180;
        img.height = 180;
        thumb.appendChild(img);
      }
      card.appendChild(thumb);

      var meta = document.createElement('span');
      meta.className = 'kop-search__meta';

      var title = document.createElement('span');
      title.className = 'kop-search__title';
      this.appendHighlighted(title, item.t, tokens);
      meta.appendChild(title);

      var price = document.createElement('span');
      price.className = 'kop-search__price';
      var amount = document.createElement('span');
      amount.className = item.sale ? 'kop-search__amount kop-search__amount--sale' : 'kop-search__amount';
      amount.textContent = item.pr;
      price.appendChild(amount);
      if (item.cp) {
        var compare = document.createElement('s');
        compare.className = 'kop-search__compare';
        compare.textContent = item.cp;
        price.appendChild(compare);
      }
      meta.appendChild(price);

      if (this.config.showRating && item.rc > 0) {
        var rating = document.createElement('span');
        rating.className = 'kop-search__rating';
        var value = item.ra % 1 === 0 ? item.ra.toFixed(1) : String(item.ra);
        rating.textContent = '\u2605 ' + value + ' (' + item.rc + ')';
        meta.appendChild(rating);
      }

      card.appendChild(meta);
      return card;
    }

    appendHighlighted(node, text, tokens) {
      /* Built with the DOM, never innerHTML, so a product title can not
         inject markup. Highlights the first matching token occurrence. */
      var lower = normalize(text);
      var plain = text;
      var best = null;
      tokens.forEach(function (token) {
        var at = lower.indexOf(token);
        if (at !== -1 && (best === null || at < best.at)) best = { at: at, len: token.length };
      });
      if (!best) { node.textContent = plain; return; }
      /* normalize() collapses punctuation, so map roughly by scanning the raw
         string for the same normalized position. Fall back to plain text when
         the mapping drifts. */
      var rawIndex = -1;
      var count = 0;
      for (var i = 0; i < plain.length; i++) {
        var ch = normalize(plain.charAt(i));
        if (ch.length === 0) continue;
        if (count === best.at) { rawIndex = i; break; }
        count += ch.length;
      }
      if (rawIndex === -1) { node.textContent = plain; return; }
      node.appendChild(document.createTextNode(plain.slice(0, rawIndex)));
      var mark = document.createElement('mark');
      mark.textContent = plain.slice(rawIndex, rawIndex + best.len);
      node.appendChild(mark);
      node.appendChild(document.createTextNode(plain.slice(rawIndex + best.len)));
    }

    /* ------------------------------------------------------------------
       Recent searches
       ------------------------------------------------------------------ */

    readRecent() {
      try {
        var list = JSON.parse(localStorage.getItem(RECENT_KEY));
        return Array.isArray(list) ? list : [];
      } catch (error) { return []; }
    }

    saveRecent(term) {
      term = term.trim();
      if (!term) return;
      var list = this.readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (error) { /* private mode */ }
      this.renderRecent();
    }

    removeRecent(term) {
      var list = this.readRecent().filter(function (t) { return t !== term; });
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (error) { /* private mode */ }
      this.renderRecent();
      this.input.focus();
    }

    renderRecent() {
      if (!this.recentList) return;
      var list = this.readRecent();
      this.recentWrap.hidden = list.length === 0;
      this.recentList.textContent = '';
      var self = this;
      list.forEach(function (term) {
        var row = document.createElement('li');
        row.className = 'kop-search__recent-row';

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kop-search__recent-term';
        btn.setAttribute('data-kop-term', term);
        btn.setAttribute('data-kop-nav', '');
        btn.textContent = term;
        row.appendChild(btn);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'kop-search__recent-remove';
        remove.setAttribute('data-kop-remove', term);
        remove.setAttribute('aria-label', self.config.copy.remove + ' ' + term);
        remove.textContent = '\u00d7';
        row.appendChild(remove);

        self.recentList.appendChild(row);
      });
    }

    /* ------------------------------------------------------------------
       Keyboard navigation
       ------------------------------------------------------------------ */

    navItems() {
      var visible = [];
      this.querySelectorAll('[data-kop-nav]').forEach(function (el) {
        if (!el.closest('[hidden]')) visible.push(el);
      });
      return visible;
    }

    activeNavItem() {
      var items = this.navItems();
      return this.navIndex >= 0 && this.navIndex < items.length ? items[this.navIndex] : null;
    }

    setNav(index) {
      var items = this.navItems();
      items.forEach(function (el) { el.classList.remove('is-highlighted'); });
      this.navIndex = index;
      if (index >= 0 && index < items.length) {
        var el = items[index];
        el.classList.add('is-highlighted');
        el.scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', el.id || '');
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(event) {
      var items;
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          items = this.navItems();
          if (items.length === 0) return;
          if (event.key === 'ArrowDown') {
            this.setNav(this.navIndex + 1 >= items.length ? 0 : this.navIndex + 1);
          } else {
            this.setNav(this.navIndex - 1 < 0 ? items.length - 1 : this.navIndex - 1);
          }
          this.escapeArmed = false;
          break;
        case 'Home':
        case 'End':
          if (this.navIndex === -1) return;
          event.preventDefault();
          items = this.navItems();
          this.setNav(event.key === 'Home' ? 0 : items.length - 1);
          break;
        case 'Escape':
          event.preventDefault();
          /* Two stage: the first press drops the highlight, the second
             closes, so a press never throws away the typed query. */
          if (this.navIndex !== -1) { this.setNav(-1); this.escapeArmed = false; return; }
          if (!this.escapeArmed && this.input.value) { this.escapeArmed = true; this.setQuery(''); return; }
          this.close();
          break;
        default:
          break;
      }
    }
  }

  customElements.define('kop-search', KopSearchElement);
})();
